import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Skeleton,
  IconButton
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
  Person as PersonIcon,
  VideoCall as VideoCallIcon,
  MoreHoriz as MoreHorizIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import {
  CommunicationLog,
  CommunicationType,
  CommunicationDirection,
  getTypeDisplayName,
  getDirectionDisplayName
} from '../../services/communicationLogService';

interface CommunicationLogsListProps {
  logs: CommunicationLog[];
  loading: boolean;
  emptyMessage?: string;
  onDelete?: (log: CommunicationLog) => void;
  showJobInfo?: boolean;
}

const CommunicationLogsList: React.FC<CommunicationLogsListProps> = ({
  logs,
  loading,
  emptyMessage = 'No communication logs found.',
  onDelete,
  showJobInfo = false
}) => {
  // Get icon component for communication type
  const getTypeIcon = (type: CommunicationType) => {
    switch (type) {
      case CommunicationType.Email:
        return <EmailIcon sx={{ fontSize: 16 }} />;
      case CommunicationType.Phone:
        return <PhoneIcon sx={{ fontSize: 16 }} />;
      case CommunicationType.SMS:
        return <SmsIcon sx={{ fontSize: 16 }} />;
      case CommunicationType.InPerson:
        return <PersonIcon sx={{ fontSize: 16 }} />;
      case CommunicationType.VideoCall:
        return <VideoCallIcon sx={{ fontSize: 16 }} />;
      default:
        return <MoreHorizIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Get border color based on direction
  const getDirectionColor = (direction: CommunicationDirection): string => {
    return direction === CommunicationDirection.Incoming ? '#2196f3' : '#4caf50';
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Grid item xs={12} key={index}>
            <Card variant="outlined" sx={{ borderLeft: '4px solid #ccc' }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={28} />
                <Box display="flex" gap={1} sx={{ mb: 1 }}>
                  <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width={150} height={24} />
                </Box>
                <Skeleton variant="text" width="90%" height={20} />
                <Skeleton variant="text" width="40%" height={20} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  // Sort logs by date (most recent first)
  const sortedLogs = [...logs].sort(
    (a, b) => new Date(b.communicationDate).getTime() - new Date(a.communicationDate).getTime()
  );

  return (
    <Grid container spacing={2}>
      {sortedLogs.map((log) => (
        <Grid item xs={12} key={log.id}>
          <Card
            variant="outlined"
            sx={{
              borderLeft: `4px solid ${getDirectionColor(log.direction)}`,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {log.subject || '(No subject)'}
                  </Typography>

                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                    <Chip
                      icon={getTypeIcon(log.type)}
                      label={getTypeDisplayName(log.type)}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={getDirectionDisplayName(log.direction)}
                      size="small"
                      sx={{
                        backgroundColor: getDirectionColor(log.direction),
                        color: 'white',
                        fontWeight: 'medium'
                      }}
                    />
                    <Box display="flex" alignItems="center" sx={{ color: 'text.secondary' }}>
                      <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="body2">
                        {formatDate(log.communicationDate)}
                      </Typography>
                    </Box>
                  </Box>

                  {log.content && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {log.content.length > 200 ? `${log.content.substring(0, 200)}...` : log.content}
                    </Typography>
                  )}

                  <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
                    {log.loggedByName && (
                      <Typography variant="caption" color="text.secondary">
                        Logged by {log.loggedByName}
                      </Typography>
                    )}
                    {showJobInfo && log.jobNumber && (
                      <Typography variant="caption" color="text.secondary">
                        Job: {log.jobNumber}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {onDelete && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(log);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default CommunicationLogsList;
